import { z } from 'zod'

import { EXAMPLE_TOPICS, type ExampleTopic } from './utils'

/** How long typing in the history search pauses before the URL follows. */
export const HISTORY_SEARCH_DEBOUNCE_MS = 300

/**
 * `/ai-analyst`'s view state. Every field optional with `.catch(undefined)`:
 * a query string is user input, and a default written into every URL would
 * make the clean view and a filtered one indistinguishable (CLAUDE.md §4.5).
 */
export const aiAnalystSearchSchema = z.object({
  q: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .optional()
    .catch(undefined)
    .describe(
      'History search: conversations whose title or message text contains it (matched passages are shown). Typing is debounced 300 ms.',
    ),
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('History store filter: only conversations pinned to this store id.'),
  tag: z
    .string()
    .trim()
    .min(1)
    .max(30)
    .optional()
    .catch(undefined)
    .describe('History tag filter: only conversations carrying this tag.'),
  topic: z
    .enum(EXAMPLE_TOPICS as [ExampleTopic, ...ExampleTopic[]])
    .optional()
    .catch(undefined)
    .describe('The "Try asking" topic tab on the welcome screen; sales by default.'),
})

export type AiAnalystSearch = z.infer<typeof aiAnalystSearchSchema>

/** A conversation's page keeps the history's filters; the topic tabs are the welcome screen's. */
export const aiAnalystConversationSearchSchema = aiAnalystSearchSchema.omit({ topic: true })

/** Either view's permission: `ai_analyst:edit` also opens the analyst. */
export const AI_ANALYST_PERMISSIONS = ['ai_analyst:view', 'ai_analyst:edit'] as const

export const DEFAULT_TOPIC: ExampleTopic = 'sales'

/** Drop what equals its default or is empty, so a URL carries only what differs. */
export function withoutAiAnalystSearchDefaults(search: AiAnalystSearch): AiAnalystSearch {
  const pruned: AiAnalystSearch = {}
  if (search.q?.trim()) pruned.q = search.q.trim()
  if (search.store !== undefined) pruned.store = search.store
  if (search.tag?.trim()) pruned.tag = search.tag.trim()
  if (search.topic !== undefined && search.topic !== DEFAULT_TOPIC) pruned.topic = search.topic
  return pruned
}
